import { useState } from "react";
import { Box, Chip } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import Text from "../../../../components/Text";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import { useGetPatientPaymentV2 } from "../../../../queries/billings/payments/use-get-patient-payment-v2";
import { CARD_BRAND_ICONS } from "../../../../pages/app/provider/patient/patient-list/patient-chart-new/financial/PatientCardDetails";
import { NonNullableDate } from "../../../../utils/date-time-utility";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  replaceUnderScoreAddSpace,
} from "../../../../utils/utils";

const SavedCards = () => {
  const [patient] = useCurrentUser();
  const patientUuid = patient?.uuid ?? "";

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [, setGridSorting] = useState<GridSortItem[]>([]);

  const {
    data: patientCardDetails,
    isFetching,
    isLoading,
  } = useGetPatientPaymentV2({
    pagination,
    typeOfPayment: "PATIENT",
    paymentMethod: "ONLINE",
    gridSorting: [],
    patientUuid,
    enabled: !!patientUuid,
  });

  const columns = applyDefaultColumnSettings<any>([
    {
      field: "displayBrand",
      headerName: "Card Number",
      flex: 1,
      minWidth: 250,
      renderCell: ({ row }) => {
        const brand = row.displayBrand?.toLowerCase() || "unknown";
        const brandIcon = CARD_BRAND_ICONS[brand];
        return (
          <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {brandIcon ? (
              <img
                width={30}
                src={brandIcon}
                alt={row.displayBrand || "card"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Text isTabletext style={{ color: "#74797B" }}>
                {replaceUnderScoreAddSpace(row.displayBrand)}
              </Text>
            )}
            {row.last4 && (
              <Box
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <Text
                  isTabletext
                  style={{ color: "#74797B", letterSpacing: "1px" }}
                >
                  •••• •••• ••••
                </Text>
                <Text
                  isTabletext
                  style={{ color: "#74797B", letterSpacing: "1px" }}
                >
                  {row.last4}
                </Text>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "cardExpiry",
      headerName: "Expiry Date",
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: "#373D41" }}>
          {row.cardExpiry || "-"}
        </Text>
      ),
    },
    {
      field: "primary",
      headerName: "Default",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: "#373D41" }}>
          {row.primary ? "Yes" : "No"}
        </Text>
      ),
    },
    {
      field: "addedOn",
      headerName: "Added On",
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: "#373D41" }}>
          {row.addedOn ? NonNullableDate(row.addedOn) : "-"}
        </Text>
      ),
    },
    {
      field: "active",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={row.active ? "Active" : "Inactive"}
          sx={{
            bgcolor: row.active ? "#E4F8E7" : "#FDECEC",
            color: row.active ? "#00B917" : "#D32F2F",
            fontWeight: 500,
          }}
        />
      ),
    },
  ]);

  return (
    <CustomDataGridV2
      getRowId={(row) => row?.uuid}
      rows={patientCardDetails?.data?.content ?? []}
      rowCount={patientCardDetails?.data?.page?.totalElements ?? 0}
      columns={columns}
      paginationModel={pagination}
      onPaginationModelChange={setPagination}
      onSortModelChange={setGridSorting}
      loading={isLoading || isFetching}
      disableRowSelectionOnClick
    />
  );
};

export default SavedCards;
